import { useNombreColegio } from '../hooks/useNombreColegio';
// src/pages/MisClasesHoy.jsx
import { useState, useEffect } from 'react';
import { EVENTO_VOLVER } from '../utils/actualizacionApp';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { horariosApi, asistenciaApi, authHelpers } from '../api/docenteApi';
import { Users, BarChart3, X, CheckCircle2, Clock, UserX, FileCheck, Calendar, CalendarDays, Lock, RefreshCw, School, AlertCircle } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import CargandoMarca from '../components/CargandoMarca';
import { formatearHora12, getVentanaMarcado } from '../utils/horas';
import { periodoDeHorarios, mensajePeriodoDocente } from '../utils/periodoEscolar';
import AvisoPeriodoDocente from '../components/AvisoPeriodoDocente';

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MisClasesHoy = () => {
  const navigate = useNavigate();
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fecha, setFecha] = useState(getTodayDate());
  const [diaSemana, setDiaSemana] = useState('');
  const [anioEscolar, setAnioEscolar] = useState(null);
  const [periodoSesion, setPeriodoSesion] = useState(null);
  const [, forceTick] = useState(0);

  // Resumen del día
  const [showResumen, setShowResumen] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [resumenLoading, setResumenLoading] = useState(false);
  const [resumenError, setResumenError] = useState('');

  // Conteos por clase: { [HorarioID]: { total, marcados } }
  // Se cargan en un solo request batch cuando horarios cambia.
  const [conteos, setConteos] = useState({});
  const [conteosLoading, setConteosLoading] = useState(false);

  // Flag del colegio que controla si se respeta el límite ±15min para marcar.
  // Default true (modo histórico). Se hidrata desde la respuesta de horarios-hoy
  // cada vez que se carga la pantalla — no requiere relogin si el admin lo cambia.
  const [respetarLimite, setRespetarLimite] = useState(true);
  const [esAcademia, setEsAcademia] = useState(false);
  const [seccionesExpandidas, setSeccionesExpandidas] = useState({});

  useEffect(() => {
    cargarHorarios();
  }, [fecha]);

  // Al volver a la app (sin recargarla) se refrescan las clases y sus conteos.
  useEffect(() => {
    const alVolver = () => { cargarHorarios(); };
    window.addEventListener(EVENTO_VOLVER, alVolver);
    return () => window.removeEventListener(EVENTO_VOLVER, alVolver);
  }, [fecha]);

  useEffect(() => {
    if (!anioEscolar || !horarios || horarios.length === 0) {
      setConteos({});
      setConteosLoading(false);
      return;
    }
    let cancelled = false;
    setConteos({});
    setConteosLoading(true);
    (async () => {
      try {
        const sesion = authHelpers.getSesion();
        if (!sesion) return;
        const docenteID = sesion.docente?.docenteID || sesion.docente?.id;
        const colegioID = sesion.colegioID;
        const data = await asistenciaApi.getConteosDia(docenteID, colegioID, anioEscolar, fecha);
        if (cancelled) return;
        const map = {};
        (data.conteos || []).forEach(c => {
          map[c.horarioID] = {
            total: !c.rosterIncompleto && Number.isInteger(c.total) && c.total >= 0 &&
              Number.isInteger(c.marcados) && c.marcados >= 0 && c.marcados <= c.total ? c.total : null,
            marcados: Number.isInteger(c.marcados) && c.marcados >= 0 ? c.marcados : null,
          };
        });
        setConteos(map);
      } catch (err) {
        // Si falla, igual quitamos el loading y mostramos los horarios sin
        // badge de conteo en vez de bloquear la pantalla indefinidamente.
        console.error('Error cargando conteos del día:', err);
      } finally {
        if (!cancelled) setConteosLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [horarios, fecha, anioEscolar]);

  useEffect(() => {
    // Re-evaluar getEstadoClase() cada 15s para que los badges cambien casi al
    // instante al cruzar el límite ("Muy temprano" → "Disponible" /
    // "Tiempo agotado") sin que el docente tenga que recargar.
    const interval = setInterval(() => {
      forceTick(n => n + 1);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // El estado "Muy temprano / No disponible" lo decide el backend (PuedeMarcar) en
  // el momento del fetch, así que el tick del cliente no lo cambia. Para que pase a
  // "Disponible" solo —sin recargar a mano— programamos UN re-fetch justo cuando
  // se abre la ventana de marcado de la próxima clase (+ un colchón).
  // Es un único setTimeout al próximo límite, no un polling continuo.
  useEffect(() => {
    if (fecha !== getTodayDate() || !horarios || horarios.length === 0) return;
    const ahora = Date.now();
    let proximo = Infinity;
    for (const h of horarios) {
      if (!h.HoraInicio || !h.DiaSemana?.includes(diaSemana)) continue;
      const inicioMargen = new Date(fecha + 'T' + getVentanaMarcado(h).inicio).getTime();
      if (inicioMargen > ahora && inicioMargen < proximo) proximo = inicioMargen;
    }
    if (proximo === Infinity) return;
    const delay = proximo - ahora + 10000; // +10s de colchón para que el backend ya lo permita
    const t = setTimeout(() => { cargarHorarios(); }, delay);
    return () => clearTimeout(t);
  }, [horarios, fecha, diaSemana]);

  const cargarHorarios = async () => {
    setLoading(true);
    setError('');

    try {
      const sesion = await authHelpers.refrescarSesion();
      
      if (!sesion) {
        navigate('/login');
        return;
      }

      const response = await horariosApi.getHorariosHoy(
        sesion.docente.docenteID || sesion.docente.id,
        sesion.colegioID,
        null,
        fecha
      );

      setAnioEscolar(periodoDeHorarios(response));
      setPeriodoSesion(sesion.docente.AnioEscolar || null);
      const todosLosHorarios = response.horarios || [];
      
      const horarioOrdenados = todosLosHorarios.sort((a, b) => {
        return (a.HoraInicio || '').localeCompare(b.HoraInicio || '');
      });
      
      setHorarios(horarioOrdenados);
      setDiaSemana(response.diaSemana || '');
      setEsAcademia(response.esAcademia === true);
      // Hidratar el flag del colegio. El backend devuelve true por default
      // cuando el campo no existe, así que aquí solo lo aplicamos cuando es
      // explícitamente boolean (defensivo contra respuestas viejas).
      if (typeof response.respetarLimiteHorasClase === 'boolean') {
        setRespetarLimite(response.respetarLimiteHorasClase);
      } else {
        setRespetarLimite(true);
      }

    } catch (err) {
      console.error('❌ ERROR cargando horarios:', err);
      setError(err.message || 'Error al cargar horarios');
      
      if (err.message.includes('Sesión') || err.message.includes('Token')) {
        authHelpers.limpiarSesion();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleIniciarClase = (horario) => {
    const simultaneos = horarios.filter((h) =>
      h.HorarioID !== horario.HorarioID && h.HoraInicio === horario.HoraInicio && h.HoraFin === horario.HoraFin
    );
    navigate('/marcar-asistencia', {
      // respetarLimite va por state para que MarcarAsistencia sepa si está
      // operando en "modo flexible". Útil para mostrar indicador al docente
      // y para cualquier check del lado cliente (la fuente de verdad sigue
      // siendo el backend, que ya respeta el flag).
      state: { horario, horariosSimultaneos: [horario, ...simultaneos], fecha, anioEscolar, respetarLimite, esAcademia },
    });
  };

  const abrirResumen = async () => {
    setShowResumen(true);
    setResumen(null);
    setResumenError('');
    setResumenLoading(true);
    try {
      const sesion = authHelpers.getSesion();
      const fechaHoy = getTodayDate();
      const horariosHoy = horarios.filter((h) => h.DiaSemana?.includes(diaSemana));

      const resultados = await Promise.all(
        horariosHoy.map((h) =>
          asistenciaApi
            .getAsistenciaClase(h.HorarioID, fecha, { colegioID: sesion?.colegioID, anioEscolar })
            .then((r) => {
              const stats = r.estadisticas;
              const campos = ['presentes', 'ausentes', 'tardanzas', 'justificados', 'total'];
              if (r.success === false || r.rosterIncompleto || r.reintentable || !stats ||
                  !campos.every(campo => Number.isInteger(stats[campo]) && stats[campo] >= 0) ||
                  stats.presentes + stats.ausentes + stats.tardanzas + stats.justificados !== stats.total) {
                throw new Error('No se pudo confirmar el resumen de todas las clases. Vuelve a intentarlo.');
              }
              return { horario: h, stats };
            })
        )
      );

      let presentes = 0;
      let ausentes = 0;
      let tardanzas = 0;
      let justificados = 0;
      let totalMarcados = 0;
      let clasesMarcadas = 0;

      resultados.forEach(({ stats }) => {
        presentes += stats.presentes;
        ausentes += stats.ausentes;
        tardanzas += stats.tardanzas;
        justificados += stats.justificados;
        totalMarcados += stats.total;
        if (stats.total > 0) clasesMarcadas += 1;
      });

      const totalAsistio = presentes + tardanzas;
      const porcentaje = totalMarcados > 0
        ? Math.round((totalAsistio / totalMarcados) * 100)
        : 0;

      setResumen({
        fecha,
        esHoy: fecha === fechaHoy,
        totalClases: horariosHoy.length,
        clasesMarcadas,
        clasesPendientes: Math.max(0, horariosHoy.length - clasesMarcadas),
        presentes,
        tardanzas,
        ausentes,
        justificados,
        totalMarcados,
        porcentaje
      });
    } catch (err) {
      console.error('Error cargando resumen:', err);
      setResumen(null);
      setResumenError(err.message || 'No se pudo leer el resumen. Vuelve a intentarlo.');
    } finally {
      setResumenLoading(false);
    }
  };

  const getEstadoClase = (horario) => {
    const ahora = new Date();

    const esDiaCorrecte = horario.DiaSemana?.includes(diaSemana);

    if (!esDiaCorrecte) {
      return {
        disponible: false,
        badge: 'badge-ghost',
        text: 'No corresponde hoy',
        Icon: Calendar,
        mensaje: `Esta clase es los días: ${horario.DiaSemana?.join(', ')}`,
        tipo: 'dia',
        orden: 4
      };
    }

    const fechaHoy = getTodayDate();
    const esHoy = fecha === fechaHoy;

    if (!esHoy) {
      return {
        disponible: false,
        badge: 'badge-info',
        text: fecha < fechaHoy ? 'Clase pasada' : 'Clase futura',
        Icon: CalendarDays,
        mensaje: `Seleccionó ${fecha}. Cambie la fecha a hoy para marcar asistencia.`,
        tipo: 'fecha',
        orden: fecha < fechaHoy ? 3 : 2
      };
    }

    const finClase = new Date(fecha + 'T' + horario.HoraFin);
    // La ventana la calcula el backend: el margen de cierre se adapta a la
    // duración del bloque, así que un bloque corto (p. ej. 15 min) se puede
    // marcar hasta su hora de fin en vez de cerrarse antes de empezar.
    const ventana = getVentanaMarcado(horario);
    const inicioConMargen = new Date(fecha + 'T' + ventana.inicio);
    const finConMargen = new Date(fecha + 'T' + ventana.fin);

    // "Muy temprano" SIEMPRE bloquea, también en modo flexible: la idea es
    // evitar pre-marcado antes de que empiece la clase.
    if (ahora < inicioConMargen) {
      // ceil: mientras falte cualquier segundo muestra ≥1 min; al cruzar el
      // límite cae a "Disponible" en vez de quedarse en "0 min" amarillo.
      const minutosRestantes = Math.ceil((inicioConMargen - ahora) / 60000);
      return {
        disponible: false,
        badge: 'badge-warning',
        text: 'Muy temprano',
        Icon: Clock,
        mensaje: `Podrá marcar en ${minutosRestantes} min (desde las ${formatearHora12(ventana.inicio)})`,
        minutosRestantes,
        tipo: 'temprano',
        orden: 2
      };
    }

    // En modo flexible (respetarLimite=false), después del inicio el docente
    // puede marcar todo el día — saltamos "Tiempo agotado".
    if (respetarLimite && ahora > finConMargen) {
      return {
        disponible: false,
        badge: 'badge-error',
        text: 'Tiempo agotado',
        Icon: Lock,
        mensaje: `Solo puede marcar hasta las ${formatearHora12(ventana.fin)}`,
        tipo: 'finalizada',
        orden: 3
      };
    }

    // Modo flexible y ya pasamos el fin de clase: queda "Disponible" sin
    // alerta informativa — el badge "Modo flexible" del header del card
    // ya explica el contexto vía tooltip.
    if (!respetarLimite && ahora > finClase) {
      return {
        disponible: true,
        badge: 'badge-success',
        text: 'Disponible',
        Icon: CheckCircle2,
        tipo: 'disponible',
        orden: 1
      };
    }

    const minutosRestantes = Math.round((finConMargen - ahora) / 60000);

    if (horario.PuedeMarcar === false) {
      return {
        disponible: false,
        badge: 'badge-warning',
        text: 'No disponible',
        Icon: Clock,
        mensaje: horario.MensajeValidacion || 'Fuera del horario permitido',
        tipo: 'temprano',
        orden: 2
      };
    }

    return {
      disponible: true,
      badge: 'badge-success',
      text: 'Disponible',
      Icon: CheckCircle2,
      mensaje: `Puede marcar asistencia ahora (quedan ${minutosRestantes} min)`,
      minutosRestantes,
      tipo: 'disponible',
      orden: 1
    };
  };

  const sesion = authHelpers.getSesion();
  const nombreDocente = sesion?.docente?.nombre?.split(' ')[0] || 
                     sesion?.docente?.nombreCompleto?.split(' ')[0] || 
                     'Docente';
  const nombreColegio = useNombreColegio();

  return (
    <div className="min-h-dvh bg-base-200 flex flex-col safe-x">
      {/* HEADER MÁS COMPACTO - SIN TEMAS */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-base-300 text-base-content shadow-lg px-5 py-3 safe-top sticky top-0 z-50"
      >
        <div className="max-w-4xl mx-auto pt-2">
          <div className="flex justify-between items-center mb-2">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold truncate leading-tight">
                Hola, {nombreDocente}
              </h1>
              <p className="text-xs opacity-70 truncate leading-tight flex items-center gap-1">
                <School className="w-3 h-3" strokeWidth={2} />
                {nombreColegio}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold leading-tight">Mis Clases</h2>
              <p className="text-xs text-base-content/60 leading-tight">
                {new Date(fecha + 'T00:00:00').toLocaleDateString('es-ES', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                })}
              </p>
            </div>
            <input
              type="date"
              value={fecha}
              onChange={(e) => {
                setFecha(e.target.value);
                setResumen(null);
              }}
              className="input input-bordered input-xs w-36"
            />
            <motion.button
              whileTap={{ scale: 0.9, rotate: 90 }}
              onClick={cargarHorarios}
              disabled={loading}
              className="btn btn-ghost btn-xs btn-circle"
              title="Actualizar"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={abrirResumen}
              disabled={horarios.length === 0}
              className="btn btn-ghost btn-xs btn-circle"
              title="Resumen del día"
            >
              <BarChart3 className="w-4 h-4" strokeWidth={2} />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* CONTENIDO - EXACTAMENTE IGUAL */}
      <div className="app-scroll flex-1 overflow-y-auto pb-20">
        <div className="container mx-auto px-5 py-4 max-w-4xl">
          {(loading || (conteosLoading && horarios.length > 0)) && (
            <CargandoMarca
              texto={loading ? 'Cargando clases del día…' : 'Ordenando por marcado…'}
            />
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="alert alert-error"
            >
              <AlertCircle className="shrink-0 w-6 h-6" strokeWidth={2} />
              <span>{error}</span>
              <button onClick={cargarHorarios} className="btn btn-sm btn-ghost">
                Reintentar
              </button>
            </motion.div>
          )}

          {!loading && !conteosLoading && !error && (
            <>
              {horarios.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card bg-base-100 shadow-md"
                >
                  <div className="card-body text-center py-12">
                    <Calendar className="w-16 h-16 mx-auto text-base-content/30 mb-3" strokeWidth={1.5} />
                    {mensajePeriodoDocente(periodoSesion, anioEscolar) ? (
                      <AvisoPeriodoDocente periodoSesion={periodoSesion} periodoHorario={anioEscolar} />
                    ) : <>
                      <h3 className="text-xl font-bold">No tiene clases hoy</h3>
                      <p className="text-base-content/60">No hay horarios programados para {diaSemana}</p>
                    </>}
                  </div>
                </motion.div>
              ) : (
                <div className="space-y-4">
                  {/* ... TODAS LAS CARDS EXACTAMENTE IGUALES ... */}
                  {horarios
                    .filter((horario, index, lista) =>
                      lista.findIndex((h) => h.HoraInicio === horario.HoraInicio && h.HoraFin === horario.HoraFin) === index
                    )
                    .map(horario => {
                      const seccionesConjuntas = horarios.filter((h) => h.HoraInicio === horario.HoraInicio && h.HoraFin === horario.HoraFin);
                      const esClaseConjunta = seccionesConjuntas.length > 1;
                      const conteo = conteos[horario.HorarioID];
                      const total = conteo?.total ?? null;
                      const marcados = conteo?.marcados ?? null;
                      const claseCompleta =
                        total !== null && total > 0 && marcados === total;
                      return {
                        horario,
                        estado: getEstadoClase(horario),
                        total,
                        marcados,
                        claseCompleta,
                        esClaseConjunta,
                        seccionesConjuntas,
                      };
                    })
                    .sort((a, b) => {
                      // Las clases ya completas van al final.
                      if (a.claseCompleta !== b.claseCompleta) {
                        return a.claseCompleta ? 1 : -1;
                      }
                      if (a.estado.orden !== b.estado.orden) {
                        return a.estado.orden - b.estado.orden;
                      }
                      return (a.horario.HoraInicio || '').localeCompare(b.horario.HoraInicio || '');
                    })
                    .map(({ horario, estado, total, marcados, claseCompleta, esClaseConjunta, seccionesConjuntas }, index) => {
                      const esBloqueCompartido = horario.BloqueCompartido === true;
                      const totalEstudiantes = total;
                      const marcadosEstudiantes = marcados;

                      return (
                        <motion.div
                          key={horario.HorarioID}
                          layout
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            // delay solo en el mount inicial, layout anima a su ritmo
                            opacity: { delay: index * 0.05, duration: 0.25 },
                            x: { delay: index * 0.05, duration: 0.25 },
                            layout: { type: 'spring', stiffness: 320, damping: 30 },
                          }}
                          className={`card bg-base-100 shadow-md hover:shadow-lg transition-shadow ${!estado.disponible && !claseCompleta ? 'opacity-75' : 'hover:scale-[1.01]'} ${esBloqueCompartido ? 'border-2 border-accent' : ''}`}
                        >
                          <div className="card-body">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  {esBloqueCompartido && (
                                    <div
                                      className="tooltip tooltip-bottom cursor-help before:!left-0 before:!translate-x-0 before:max-w-[min(260px,calc(100vw-2rem))] before:whitespace-pre-wrap before:text-left"
                                      data-tip='Verás el listado completo de la sección. Usa "Omitir para el siguiente grupo" en los estudiantes que no te toca marcar, y marcalos cuando te corresponda.'
                                    >
                                      <div className="badge badge-accent gap-1">
                                        <Users className="w-3 h-3" />
                                        Bloque compartido
                                      </div>
                                    </div>
                                  )}

                                  {!respetarLimite && (
                                    <div
                                      className="tooltip tooltip-bottom cursor-help before:!left-0 before:!translate-x-0 before:max-w-[min(260px,calc(100vw-2rem))] before:whitespace-pre-wrap before:text-left"
                                      data-tip="Modo flexible activado: tu colegio permite marcar asistencia en cualquier momento del día (después del inicio de cada clase)."
                                    >
                                      <div className="badge badge-success gap-1">
                                        <Clock className="w-3 h-3" />
                                        Modo flexible
                                      </div>
                                    </div>
                                  )}

                                  <h3 className={`card-title text-lg ${claseCompleta ? 'opacity-50' : ''}`}>
                                    {horario.Materia}
                                  </h3>
                                  {esClaseConjunta && (
                                    <div className="badge badge-warning badge-sm gap-1" title="Estas secciones se marcan en una sola lista">
                                      <Users className="w-3 h-3" strokeWidth={2} />
                                      Clase conjunta
                                    </div>
                                  )}

                                  {esClaseConjunta && (
                                    <div className="w-full text-sm text-base-content/70">
                                      <button type="button" className="btn btn-ghost btn-xs gap-1 px-0 text-primary"
                                        onClick={() => setSeccionesExpandidas((prev) => ({ ...prev, [horario.HorarioID]: !prev[horario.HorarioID] }))}>
                                        <Users className="w-3.5 h-3.5" strokeWidth={2} />
                                        {seccionesConjuntas.length} secciones {seccionesExpandidas[horario.HorarioID] ? '▲' : '▼'}
                                      </button>
                                      {seccionesExpandidas[horario.HorarioID] && (
                                        <div className="mt-1 rounded-lg bg-base-200 px-2 py-1.5 text-xs space-y-1">
                                          {seccionesConjuntas.map((clase) => <p key={clase.HorarioID}>{clase.NivelAcademico} {clase.Seccion} <span className="opacity-60">— {clase.Materia}</span></p>)}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* En clase completa el badge de estado pierde relevancia: lo
                                      atenuamos para que destaque el "Clase marcada". */}
                                  <div className={`badge ${estado.badge} badge-sm gap-1 ${claseCompleta ? 'opacity-50' : ''}`}>
                                    {estado.Icon && <estado.Icon className="w-3 h-3" strokeWidth={2} />}
                                    {estado.text}
                                    {estado.minutosRestantes !== undefined && estado.disponible && (
                                      <span className="font-bold"> • {estado.minutosRestantes}m</span>
                                    )}
                                  </div>

                                  {totalEstudiantes !== null && totalEstudiantes > 0 && (
                                    claseCompleta ? (
                                      <div className="badge badge-success gap-1 font-semibold" title="Todos los estudiantes marcados">
                                        <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                                        Clase marcada
                                      </div>
                                    ) : (
                                      <div
                                        className="badge badge-outline badge-sm font-semibold"
                                        title={`${marcadosEstudiantes} de ${totalEstudiantes} estudiantes marcados`}
                                      >
                                        {marcadosEstudiantes}/{totalEstudiantes}
                                      </div>
                                    )
                                  )}
                                </div>
                                
                                <div className={`space-y-1 text-sm text-base-content/70 ${claseCompleta ? 'opacity-50 grayscale' : ''}`}>
                                  {!esClaseConjunta && (
                                    <p><span className="font-semibold">Sección:</span> {horario.NivelAcademico} {horario.Seccion}</p>
                                  )}
                                  <p>
                                    <span className="font-semibold">Horario:</span> {formatearHora12(horario.HoraInicio)} – {formatearHora12(horario.HoraFin)}
                                  </p>
                                  <p>
                                    <span className="font-semibold">Día:</span>{' '}
                                    <span className="badge badge-sm badge-primary">
                                      {diaSemana}
                                    </span>
                                  </p>
                                  
                                  {estado.mensaje && (
                                    <div className={`alert py-2 mt-2 ${
                                      estado.tipo === 'disponible' ? 'alert-success' :
                                      estado.tipo === 'temprano' ? 'alert-warning' :
                                      estado.tipo === 'finalizada' ? 'alert-error' :
                                      estado.tipo === 'dia' ? 'alert-info' :
                                      'alert-info'
                                    }`}>
                                      <AlertCircle className="shrink-0 w-4 h-4" strokeWidth={2} />
                                      <span className="text-xs">{estado.mensaje}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="card-actions justify-end mt-4">
                              <button
                                onClick={() => handleIniciarClase(horario)}
                                disabled={!estado.disponible && !claseCompleta}
                                className={`btn ${
                                  claseCompleta
                                    ? 'btn-success'
                                    : estado.disponible
                                      ? (esBloqueCompartido ? 'btn-accent' : 'btn-primary')
                                      : 'btn-disabled'
                                }`}
                              >
                                {claseCompleta ? (
                                  <>
                                    <FileCheck className="w-4 h-4" strokeWidth={2} />
                                    Ver Resumen
                                  </>
                                ) : estado.disponible ? (
                                  <>
                                    <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                                    Marcar Asistencia
                                  </>
                                ) : (
                                  <>
                                    {estado.Icon && <estado.Icon className="w-4 h-4" strokeWidth={2} />}
                                    {estado.text}
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                </div>
              )}
            </>
          )}

          {!loading && horarios.length > 0 && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center mt-6"
            >
              <button
                onClick={cargarHorarios}
                className="btn btn-ghost btn-sm gap-1"
              >
                <RefreshCw className="w-4 h-4" strokeWidth={2} />
                Actualizar
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Modal Resumen del Día */}
      <AnimatePresence>
        {showResumen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-4"
            onClick={() => setShowResumen(false)}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-base-100 rounded-3xl w-full max-w-sm overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <div>
                  <h3 className="font-bold text-lg leading-tight">Resumen del día</h3>
                  <p className="text-xs text-base-content/60">
                    {new Date(fecha + 'T00:00:00').toLocaleDateString('es-ES', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long'
                    })}
                  </p>
                </div>
                <button
                  onClick={() => setShowResumen(false)}
                  className="btn btn-ghost btn-sm btn-circle"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>

              <div className="px-5 pb-5 space-y-4">
                {resumenLoading && (
                  <div className="flex justify-center py-8">
                    <span className="loading loading-spinner loading-md text-primary" />
                  </div>
                )}

                {!resumenLoading && resumenError && (
                  <div role="alert" className="rounded-2xl bg-error/10 p-4 space-y-3">
                    <p className="text-sm">{resumenError}</p>
                    <button className="btn btn-sm btn-outline" onClick={abrirResumen}>
                      <RefreshCw className="w-4 h-4" /> Reintentar
                    </button>
                  </div>
                )}

                {!resumenLoading && !resumenError && resumen && (
                  <>
                    {/* Asistencia del día */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl bg-primary/5 p-4"
                    >
                      <div className="flex items-end justify-between mb-3">
                        <div>
                          <p className="text-xs text-base-content/60">Asistencia</p>
                          <p className="text-3xl font-bold text-primary leading-none">
                            {resumen.porcentaje}%
                          </p>
                        </div>
                        <p className="text-xs text-base-content/60 text-right">
                          {resumen.presentes + resumen.tardanzas} de{' '}
                          {resumen.totalMarcados} marcados
                        </p>
                      </div>
                      <progress
                        className="progress progress-primary w-full"
                        value={resumen.porcentaje}
                        max="100"
                      />
                    </motion.div>

                    {/* Clases marcadas/pendientes */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 }}
                      className="grid grid-cols-2 gap-3"
                    >
                      <div className="rounded-2xl bg-success/10 p-3">
                        <CheckCircle2 className="w-4 h-4 text-success mb-1" strokeWidth={2} />
                        <p className="text-2xl font-bold text-success leading-none">
                          {resumen.clasesMarcadas}
                        </p>
                        <p className="text-xs text-base-content/60 mt-1">
                          Clases marcadas
                        </p>
                      </div>
                      <div className="rounded-2xl bg-warning/10 p-3">
                        <Clock className="w-4 h-4 text-warning mb-1" strokeWidth={2} />
                        <p className="text-2xl font-bold text-warning leading-none">
                          {resumen.clasesPendientes}
                        </p>
                        <p className="text-xs text-base-content/60 mt-1">
                          Pendientes
                        </p>
                      </div>
                    </motion.div>

                    {/* Desglose */}
                    {resumen.totalMarcados > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="rounded-2xl bg-base-200 p-4 space-y-2"
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-success" strokeWidth={2} />
                            Presentes
                          </span>
                          <span className="font-bold">{resumen.presentes}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-warning" strokeWidth={2} />
                            Tardanzas
                          </span>
                          <span className="font-bold">{resumen.tardanzas}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <UserX className="w-4 h-4 text-error" strokeWidth={2} />
                            Ausentes
                          </span>
                          <span className="font-bold">{resumen.ausentes}</span>
                        </div>
                        {resumen.justificados > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <FileCheck className="w-4 h-4 text-info" strokeWidth={2} />
                              Justificados
                            </span>
                            <span className="font-bold">{resumen.justificados}</span>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {resumen.totalClases === 0 && (
                      <p className="text-center text-sm text-base-content/60 py-4">
                        No hay clases programadas para este día.
                      </p>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default MisClasesHoy;
